import { Fragment, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import { EllipsisVerticalIcon } from "@heroicons/react/20/solid";
import {
  ArchiveBoxIcon,
  ArrowPathIcon,
  ViewfinderCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import ArchiveLibraryRequestModal from "../LibraryRequests/ArchiveLibraryRequestModal";
import CancelAssessmentModal from "../CancelAssessmentModal";
import Notification from "../Notification";
import {
  useArchiveVqaRequest,
  useCancelAssessment,
  useUnarchiveVqaRequest,
} from "../../custom-hooks/queries";
import {
  buildSponsorScopedDetailsUrl,
  requestTypeToStatusParam,
} from "../../utils/sponsorScopedDetailsUrl";
import {
  canCancelCalendarAssessment,
  canJoinCalendarAssessment,
} from "../../utils/assessmentCalendarRules";
import { isCalendarJoinOnlyRow } from "../../utils/assessmentCalendarNavigation";

function rowRequestId(row) {
  return row?.vqa_request_id ?? row?.Vqa_Request_Id ?? row?.vqaRequestId;
}

function rowVqaId(row) {
  return row?.vqa_id ?? row?.vqaId;
}

function rowStatus(row) {
  return String(row?.vqa_request_status ?? row?.vqa_status ?? "").trim();
}

export function sponsorsForCancel(row) {
  if (Array.isArray(row?.vqa_request_sponsors) && row.vqa_request_sponsors.length) {
    return row.vqa_request_sponsors;
  }
  if (row?.sponsor_id || row?.sponsor_name) {
    return [
      {
        sponsor_id: row.sponsor_id,
        sponsor_name: row.sponsor_name,
        sponsor_status: row.vqa_status,
      },
    ];
  }
  return [];
}

function menuItemClass(active, danger) {
  if (danger) return active ? "bg-red-50 text-red-900" : "text-red-600";
  return active ? "bg-gray-100 text-gray-900" : "text-gray-700";
}

function CalendarMenuAction({ label, icon: Icon, onSelect, danger = false, disabled = false }) {
  const iconClass = danger
    ? "text-red-400 group-hover:text-red-500"
    : "text-gray-400 group-hover:text-gray-500";
  return (
    <Menu.Item>
      {({ active }) => (
        <button
          type="button"
          disabled={disabled}
          className={`${menuItemClass(active, danger)} group flex w-full items-center px-4 py-2 text-sm disabled:opacity-50`}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onSelect();
          }}
        >
          {Icon ? <Icon className={`mr-3 h-5 w-5 ${iconClass}`} aria-hidden="true" /> : null}
          {label}
        </button>
      )}
    </Menu.Item>
  );
}

export default function CalendarAssessmentActionMenu({
  row,
  isAdmin,
  onOpen,
  onRequestToJoin,
  refetch,
}) {
  const navigate = useNavigate();
  const requestId = rowRequestId(row);
  const vqaId = rowVqaId(row);
  const status = rowStatus(row);
  const isArchived = status.toLowerCase() === "archived";

  const [archiveOpen, setArchiveOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [note, setNote] = useState({ show: false, message: "", status: "success" });

  const { mutate: archiveMutate, isPending: archivePending } = useArchiveVqaRequest();
  const { mutate: unarchiveMutate, isPending: unarchivePending } = useUnarchiveVqaRequest();
  const { mutate: cancelMutate } = useCancelAssessment();

  const notifyResult = (successMessage, fallbackError, onSettled) => ({
    onSuccess: () => {
      onSettled?.();
      setNote({ show: true, message: successMessage, status: "success" });
      refetch?.();
    },
    onError: (err) => {
      onSettled?.();
      setNote({
        show: true,
        message: err?.response?.data?.message || fallbackError,
        status: "error",
      });
    },
  });

  const handleViewDetails = () => {
    if (isAdmin && requestId != null) {
      navigate(
        buildSponsorScopedDetailsUrl(
          `/Backend/Assessment/${requestId}/Manage`,
          row?.sponsor_name,
          requestTypeToStatusParam(row?.request_type)
        )
      );
      return;
    }
    onOpen?.(row);
  };

  const showRequestToJoin =
    !isAdmin && canJoinCalendarAssessment(row) && onRequestToJoin;
  const showCancel = isAdmin && vqaId != null && canCancelCalendarAssessment(status);
  const showArchive = isAdmin && requestId != null;
  // A sponsor not on the assessment gets Request to Join only, never View Details.
  const joinOnly = isCalendarJoinOnlyRow(row, { isAdmin });
  if (joinOnly && !showRequestToJoin) return null;

  return (
    <>
      <Menu as="div" className="relative inline-block text-left" data-dropdown-menu>
        <div>
          <Menu.Button
            className="-m-2 inline-flex items-center justify-center rounded-full p-2 text-gray-500 hover:text-gray-600 focus:outline-none"
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <span className="sr-only">Open options</span>
            <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
          </Menu.Button>
        </div>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items
            portal
            anchor="bottom end"
            className="z-[100] mt-2 w-56 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none"
          >
            {joinOnly ? null : (
              <CalendarMenuAction
                label="View Details"
                icon={ViewfinderCircleIcon}
                onSelect={handleViewDetails}
              />
            )}
            {showRequestToJoin ? (
              <CalendarMenuAction label="Request to Join" onSelect={() => onRequestToJoin(row)} />
            ) : null}
            {showCancel ? (
              <CalendarMenuAction
                label="Cancel Assessment"
                icon={XCircleIcon}
                danger
                onSelect={() => setCancelOpen(true)}
              />
            ) : null}
            {showArchive && isArchived ? (
              <CalendarMenuAction
                label="Unarchive Request"
                icon={ArrowPathIcon}
                disabled={unarchivePending}
                onSelect={() =>
                  unarchiveMutate(
                    requestId,
                    notifyResult("Request unarchived successfully.", "Could not unarchive this request.")
                  )
                }
              />
            ) : null}
            {showArchive && !isArchived ? (
              <>
                <div className="my-1 border-t border-gray-100" />
                <CalendarMenuAction
                  label="Archive Request"
                  icon={ArchiveBoxIcon}
                  onSelect={() => setArchiveOpen(true)}
                />
              </>
            ) : null}
          </Menu.Items>
        </Transition>
      </Menu>

      <ArchiveLibraryRequestModal
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        isSubmitting={archivePending}
        requestLabel={row?.provider_name || ""}
        onConfirm={(archiveComment) =>
          archiveMutate(
            { vqaRequestId: requestId, archiveComment },
            notifyResult("Request archived successfully.", "Could not archive this request.", () =>
              setArchiveOpen(false)
            )
          )
        }
      />

      <CancelAssessmentModal
        open={cancelOpen}
        setOpen={setCancelOpen}
        id={vqaId}
        sponsors={sponsorsForCancel(row)}
        onClick={cancelMutate}
        refetch={() => {
          refetch?.();
          setCancelOpen(false);
        }}
      />

      <Notification
        setShow={(show) => setNote((prev) => ({ ...prev, show }))}
        show={note.show}
        status={note.status}
        errorMessage={note.message}
      />
    </>
  );
}
